import { useCallback, useEffect, useRef, useState } from 'react';
import { useFirebase } from 'v3/hooks/useFirebase';
import { SseStatus } from './types';

export const useSse = (url: string, desiredResult?: string) => {
  const [data, setData] = useState<any>(null);
  const [status, setStatus] = useState<SseStatus>('closed');
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const { getToken } = useFirebase();
  const baseUrl = import.meta.env.VITE_FINANCING_API_BASE_URL;

  const buildUrl = useCallback(async () => {
    const token = await getToken();
    return `${baseUrl}${url}/?token=${token}`;
  }, [baseUrl, url, getToken]);

  const connect = useCallback(async () => {
    if (!url || eventSourceRef.current) return;

    setData(null);
    setError(null);
    setStatus('connecting');

    try {
      const authenticatedUrl = await buildUrl();
      const eventSource = new EventSource(authenticatedUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setStatus('open');
      };

      eventSource.onmessage = (event: MessageEvent) => {
        if (event.data && !event.data.includes('heartbeat')) {
          try {
            const messagePayload = JSON.parse(event.data);
            setData(messagePayload);
            if (Boolean(desiredResult) && event.data.includes(desiredResult)) {
              eventSource.close();
              eventSourceRef.current = null;
              setStatus('closed');
            }
          } catch {
            setError('Failed to parse SSE data.');
            eventSource.close();
            eventSourceRef.current = null;
            setStatus('closed');
          }
        }
      };

      eventSource.onerror = () => {
        setError('Error in SSE connection.');
        setStatus('closed');
      };
    } catch {
      setError('Error initializing SSE.');
      setStatus('closed');
    }
  }, [url, desiredResult, buildUrl]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setStatus('closed');
    }
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return { data, status, error, connect, disconnect };
};
